import { useMemo } from 'react';
import type { MailboxSearchRouteQuery, MemberExternalAccountSafe } from '@vivport/backend/types';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MailboxEnvelopeRow } from '@/components/mailbox/MailboxEnvelopeRow';
import { useUser } from '@/hooks';
import {
  MAILBOX_SEARCH_PAGE_SIZE,
  useMailboxImportedRefs,
  useMailboxSearchInfinite,
  useMemberExternalAccounts,
} from '@/hooks/useMemberExternalAccountQueries';
import { resolveKeptRef } from '@/components/keep/kept-ref';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { hasSearchableText, useLiveSearchGate } from '@/utils/explore-live-search';
import { mailboxEnvelopeRowData } from '@/utils/mailbox-envelope-row';

const BROWSER_TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

function hasSearchableMailboxQuery(searchText: string | undefined, after: string | undefined, before: string | undefined): boolean {
  return hasSearchableText(searchText ?? '') || after !== undefined || before !== undefined;
}

export interface ExploreMailboxResultsProps {
  mailbox: boolean;
  searchText: string;
  after?: string;
  before?: string;
}

interface ExploreMailboxAccountBlockProps {
  account: MemberExternalAccountSafe;
  searchText: string;
  after?: string;
  before?: string;
}

function ExploreMailboxAccountBlock({ account, searchText, after, before }: ExploreMailboxAccountBlockProps) {
  const params = useMemo<MailboxSearchRouteQuery>(
    () => ({
      ...(searchText ? { text: searchText } : {}),
      ...(after ? { after } : {}),
      ...(before ? { before } : {}),
      timeZone: BROWSER_TIME_ZONE,
      maxResults: MAILBOX_SEARCH_PAGE_SIZE,
    }),
    [searchText, after, before]
  );
  const gate = useLiveSearchGate(params, (candidate) =>
    hasSearchableMailboxQuery(candidate.text, candidate.after, candidate.before)
  );
  const searchQuery = useMailboxSearchInfinite(account.id, gate.params, gate.ready);
  const envelopes = useMemo(
    () => (searchQuery.data?.pages ?? []).flatMap((page) => page.messages),
    [searchQuery.data?.pages]
  );
  const ids = useMemo(() => envelopes.map((envelope) => envelope.providerMessageId), [envelopes]);
  const refsQuery = useMailboxImportedRefs(account.id, ids);
  const providerLabel = account.productLabel;
  const address = account.externalEmail;

  return (
    <div className="mt-3">
      <div className="flex items-center gap-2">
        <p className="min-w-0 truncate text-xs text-muted-foreground">
          {providerLabel} · {address}
          {searchQuery.data !== undefined ? ` · ${envelopes.length} shown` : ''}
        </p>
        {searchQuery.hasNextPage ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="ml-auto"
            disabled={searchQuery.isFetchingNextPage}
            onClick={() => void searchQuery.fetchNextPage()}
          >
            {searchQuery.isFetchingNextPage ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
            More
          </Button>
        ) : null}
      </div>
      {!gate.ready || searchQuery.isPending ? (
        <p className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Searching {address}…
        </p>
      ) : searchQuery.isError ? (
        <p className="mt-1 text-xs text-destructive">
          {getAxiosErrorDisplayMessage(searchQuery.error, 'Mailbox search failed')}
        </p>
      ) : envelopes.length === 0 ? (
        <p className="mt-1 text-muted-foreground">Nothing in {address} matched</p>
      ) : (
        <ul className="mt-1 divide-y divide-border">
          {envelopes.map((envelope) => (
            <MailboxEnvelopeRow
              key={envelope.providerMessageId}
              row={mailboxEnvelopeRowData(envelope)}
              accountId={account.id}
              providerLabel={providerLabel}
              kept={resolveKeptRef(
                refsQuery.data,
                envelope.providerMessageId,
                envelope.importedContentId,
                envelope.importedCode
              )}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

export function ExploreMailboxResults({ mailbox, searchText, after, before }: ExploreMailboxResultsProps) {
  const { data: memberContext } = useUser();
  const memberId = memberContext?.selectedMember?.id ?? 0;
  const accountsQuery = useMemberExternalAccounts(memberId);
  const mailAccounts = useMemo(
    () => (accountsQuery.data?.items ?? []).filter((account) => account.isActive),
    [accountsQuery.data?.items]
  );

  if (!mailbox || mailAccounts.length === 0) {
    return null;
  }

  const hasQuery = hasSearchableMailboxQuery(searchText, after, before);

  return (
    <div className="mt-6 border-t border-border pt-4 text-sm">
      <h2 className="font-medium">In your mailbox</h2>
      {hasQuery ? (
        mailAccounts.map((account) => (
          <ExploreMailboxAccountBlock
            key={account.id}
            account={account}
            searchText={searchText}
            after={after}
            before={before}
          />
        ))
      ) : (
        <p className="mt-2 text-muted-foreground">Add words or a date to search your mailbox</p>
      )}
    </div>
  );
}
